import React, { useEffect, useState } from "react";
import { MailWarning, Search, Send, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Section = ({ title, data, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E8ECE9] p-4" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-sm font-medium text-[#11211A]">{title}</span>
        <span className="inline-flex items-center gap-2 text-sm">
          <span className="font-serif text-2xl text-[#264839]" data-testid={`${testId}-count`}>{data.count}</span>
          {data.count > 0 && (open ? <ChevronDown className="w-4 h-4 text-[#7D8C85]" /> : <ChevronRight className="w-4 h-4 text-[#7D8C85]" />)}
        </span>
      </button>
      <p className="text-xs text-[#7D8C85] mt-1">{data.note}</p>
      {data.skipped_already_claimed > 0 && <p className="text-xs text-[#7D8C85] mt-1">{data.skipped_already_claimed} skipped — already claimed since.</p>}
      {data.excluded_complete > 0 && <p className="text-xs text-[#264839] mt-1" data-testid={`${testId}-excluded-complete`}>{data.excluded_complete} excluded — now 100% set up, will not be re-nudged.</p>}
      {open && data.rows.length > 0 && (
        <ul className="mt-3 max-h-56 overflow-y-auto text-xs divide-y divide-[#F0EFEA]">
          {data.rows.map((r) => (
            <li key={r.id} className="py-1.5 flex flex-wrap justify-between gap-2">
              <span className="text-[#11211A]">{r.company} <span className="text-[#7D8C85]">· {r.email} · {r.tier}{r.backfilled ? " · backfilled stamp" : ""}{r.percent_now != null ? ` · now ${r.percent_now}%` : ""}{r.pct_at_send != null && r.pct_at_send !== "" ? ` (was ${r.pct_at_send}%)` : ""}</span></span>
              <span className="text-[#7D8C85]">{(r.stamped_at || "").slice(0, 16).replace("T", " ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RowList = ({ rows }) => (
  <ul className="mt-3 max-h-56 overflow-y-auto text-xs divide-y divide-[#F0EFEA]">
    {rows.map((r) => (
      <li key={r.id} className="py-1.5 flex flex-wrap justify-between gap-2">
        <span className="text-[#11211A]">{r.company} <span className="text-[#7D8C85]">· {r.email} · {r.tier}</span></span>
        <span className="text-[#7D8C85]">{(r.stamped_at || "").slice(0, 16).replace("T", " ")}</span>
      </li>
    ))}
  </ul>
);

const ClaimGroup = ({ label, rows, count, tone, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border p-3 ${tone}`} data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left" disabled={count === 0}>
        <span className="text-xs uppercase tracking-widest">{label}</span>
        <span className="inline-flex items-center gap-2">
          <span className="font-serif text-xl" data-testid={`${testId}-count`}>{count}</span>
          {count > 0 && (open ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />)}
        </span>
      </button>
      {open && rows.length > 0 && <RowList rows={rows} />}
    </div>
  );
};

const ClaimsSection = ({ data }) => {
  const days = data.min_age_days ?? 7;
  const held = data.held_back || { count: 0, rows: [] };
  return (
    <div className="border border-[#E8ECE9] p-4" data-testid="recovery-claims">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#11211A]">Claim links to re-send now</span>
        <span className="font-serif text-2xl text-[#264839]" data-testid="recovery-claims-count">{data.count}</span>
      </div>
      <p className="text-xs text-[#7D8C85] mt-1">{data.note}</p>
      {data.skipped_already_claimed > 0 && <p className="text-xs text-[#7D8C85] mt-1">{data.skipped_already_claimed} skipped — already claimed since.</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <ClaimGroup label={`Ready — sent ${days}+ days ago`} rows={data.rows} count={data.count} tone="border-[#264839]/40 bg-[#F3F7F5] text-[#264839]" testId="recovery-claims-ready" />
        <ClaimGroup label={`Held back — sent in the last ${days} days`} rows={held.rows} count={held.count} tone="border-[#B8860B]/40 bg-[#FFFBEB] text-[#7A5A08]" testId="recovery-claims-held" />
      </div>
      {held.count > 0 && <p className="text-xs text-[#7A5A08] mt-2" data-testid="recovery-claims-held-note">{held.count} held back — not sent on apply. They will move to Ready automatically once their link is {days} days old; just run Preview again later.</p>}
    </div>
  );
};

const TestAccountsPurge = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/test-accounts"); setInfo(data); } catch (_) { toast.error("Check failed"); } finally { setBusy(false); }
  };
  const purge = async () => {
    if (!info?.count || !window.confirm(`Permanently delete ${info.count} QA accounts on reserved test domains (and their listings)?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/test-accounts/purge", { confirm: "PURGE" }); toast.success(`Purged: ${data.result}`); setInfo({ count: 0, emails: [] }); } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-t border-[#E8ECE9] pt-4 flex flex-wrap items-center gap-3" data-testid="test-accounts-purge">
      <div className="text-sm text-[#4A5550]">QA accounts on reserved test domains (example.com, e1test.com, test.com):</div>
      <button onClick={check} disabled={busy} className="text-xs uppercase tracking-widest border border-[#C4CFC9] text-[#4A5550] px-3 py-1.5 hover:border-[#264839] transition disabled:opacity-50" data-testid="test-accounts-check-btn">Check</button>
      {info && <span className="text-sm font-medium text-[#11211A]" data-testid="test-accounts-count">{info.count} found</span>}
      {info?.count > 0 && <button onClick={purge} disabled={busy} className="text-xs uppercase tracking-widest bg-[#B35C4A] text-white px-3 py-1.5 hover:bg-[#9E4E3E] transition disabled:opacity-50" data-testid="test-accounts-purge-btn">Purge now</button>}
    </div>
  );
};

export const SafeModeRecoveryPanel = () => {
  const [since, setSince] = useState("2026-08-12");
  const [includeBackfilled, setIncludeBackfilled] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!job || job.status === "done" || job.status === "failed") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/safe-mode-recovery/jobs/${job.id}`);
        setJob(data);
        if (data.status === "done") { setResult(data); toast.success("Recovery finished"); }
        if (data.status === "failed") toast.error(`Recovery failed: ${data.error}`);
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [job]);

  const runPreview = async () => {
    setBusy("preview"); setResult(null);
    try {
      const { data } = await api.get(`/admin/safe-mode-recovery/preview?since=${since}&include_backfilled=${includeBackfilled}`);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setBusy(""); }
  };

  const runApply = async () => {
    if (!preview || preview.total === 0) return;
    const held = preview.claim_links.held_back?.count || 0;
    if (!window.confirm(`Reset ${preview.founding_welcome.count} Founding welcome stamps, ${preview.stalled_nudge.count} stalled-nudge stamps and re-send ${preview.claim_links.count} claim links (sent 7+ days ago) now?${held ? ` ${held} more recent claim links will be held back.` : ""} Real emails will go out.`)) return;
    setBusy("apply");
    try {
      const { data } = await api.post("/admin/safe-mode-recovery/apply", { since, include_backfilled: includeBackfilled, confirm: "RESEND" });
      setPreview(null);
      setJob({ id: data.job_id, status: data.status, totals: data.totals, progress: { founding_reset: 0, stalled_reset: 0, claims_done: 0, claims_sent: 0, claims_failed: 0 } });
      toast.success("Recovery queued — processing in the background");
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 bg-white border border-[#E8ECE9] p-8" data-testid="safe-mode-recovery-panel">
      <div className="flex items-center gap-2">
        <MailWarning className="w-5 h-5 text-[#B35C4A]" />
        <div className="font-serif text-xl text-[#11211A]">Safe-mode recovery</div>
      </div>
      <p className="text-sm text-[#7D8C85] mt-2">Finds emails that were stamped as sent while <code>EMAIL_SAFE_MODE</code> was on (redirected to the safe inbox) and lets you re-send them once. Preview first — nothing changes until you apply.</p>
      <div className="flex flex-wrap items-end gap-3 mt-5">
        <div>
          <label className="block text-xs text-[#7D8C85] mb-1">Safe mode was on since</label>
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="recovery-since" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A5550] pb-2">
          <input type="checkbox" checked={includeBackfilled} onChange={(e) => setIncludeBackfilled(e.target.checked)} data-testid="recovery-include-backfilled" />
          Include backfilled Founding stamps
        </label>
        <button onClick={runPreview} disabled={!!busy} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest border border-[#264839] text-[#264839] px-4 py-2.5 hover:bg-[#264839] hover:text-white transition disabled:opacity-50" data-testid="recovery-preview-btn">
          <Search className="w-3.5 h-3.5" /> {busy === "preview" ? "Checking…" : "Preview affected records"}
        </button>
      </div>

      {preview && (
        <div className="mt-6 space-y-3" data-testid="recovery-preview">
          <Section title="Founding welcome emails to re-send" data={preview.founding_welcome} testId="recovery-founding" />
          <Section title="Stalled-onboarding nudges to reset" data={preview.stalled_nudge} testId="recovery-stalled" />
          <ClaimsSection data={preview.claim_links} />
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="text-sm text-[#11211A]"><span className="font-serif text-2xl text-[#264839]" data-testid="recovery-total">{preview.total}</span> records affected</div>
            <button onClick={runApply} disabled={!!busy || preview.total === 0} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-[#B35C4A] text-white px-4 py-2.5 hover:bg-[#9E4E3E] transition disabled:opacity-50" data-testid="recovery-apply-btn">
              <Send className="w-3.5 h-3.5" /> {busy === "apply" ? "Applying…" : "Apply & re-send"}
            </button>
          </div>
        </div>
      )}

      <TestAccountsPurge />
      {job && job.status !== "done" && (
        <div className="mt-6 bg-[#FFFBEB] border border-[#B8860B]/40 p-4 text-sm text-[#11211A]" data-testid="recovery-job-progress">
          <div className="font-medium">{job.status === "failed" ? "Recovery failed" : "Recovery running in the background…"} <span className="text-xs text-[#7D8C85]">job {job.id.slice(0, 8)}</span></div>
          <ul className="mt-1 list-disc pl-5 space-y-0.5 text-[#4A5550]">
            <li>Founding welcome stamps reset: {job.progress?.founding_reset ?? 0} / {job.totals?.founding ?? 0}</li>
            <li>Stalled-nudge stamps reset: {job.progress?.stalled_reset ?? 0} / {job.totals?.stalled ?? 0}</li>
            <li>Claim links re-sent: {job.progress?.claims_done ?? 0} / {job.totals?.claims ?? 0}{job.progress?.claims_failed ? ` (${job.progress.claims_failed} failed)` : ""}</li>
          </ul>
          {job.error && <div className="text-xs mt-2 text-[#B35C4A]">{job.error}</div>}
          <div className="text-xs text-[#7D8C85] mt-2">You can leave this page — progress is saved and visible in the audit log.</div>
        </div>
      )}
      {result && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900" data-testid="recovery-result">
          <div className="font-medium">Done.</div>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            <li>{result.progress?.founding_reset ?? 0} Founding welcome stamps reset — sweep sent {result.founding_sweep?.sent ?? 0} just now</li>
            <li>{result.progress?.stalled_reset ?? 0} stalled-nudge stamps reset — re-sent within 60 min if still stalled</li>
            <li>{result.progress?.claims_sent ?? 0} claim links re-sent{result.claims_failed?.length ? `, ${result.claims_failed.length} failed` : ""}</li>
          </ul>
          {result.claims_failed?.length > 0 && <div className="text-xs mt-2 text-[#B35C4A]">{result.claims_failed.map((f) => `${f.email}: ${f.error}`).join(" · ")}</div>}
        </div>
      )}
    </div>
  );
};

export default SafeModeRecoveryPanel;
